import type { VehicleHandlingTuning } from './vehicle';
import { getDriverProgress, rankForXp } from '../save/driverProgress';

export type DriverLoadoutId = 'balanced' | 'grip' | 'drift' | 'velocity';

export type DriverLoadout = {
  id: DriverLoadoutId;
  name: string;
  unlockRank: number;
  tagline: string;
  perk: string;
  paintColor: number;
  accentColor: number;
  handling: VehicleHandlingTuning;
};

export const DRIVER_LOADOUT_STORAGE_KEY = 'lm-selected-driver-loadout';

const LOADOUTS: readonly DriverLoadout[] = [
  {
    id: 'balanced',
    name: 'Ferrari Courier',
    unlockRank: 1,
    tagline: 'Balanced street setup',
    perk: 'Stable acceleration, steering, and nitro.',
    paintColor: 0xd0162d,
    accentColor: 0x00e5ff,
    handling: {},
  },
  {
    id: 'grip',
    name: 'Apex Grip',
    unlockRank: 3,
    tagline: 'Precision cornering',
    perk: 'Sharper steering and stronger road grip.',
    paintColor: 0x0f5cff,
    accentColor: 0x8ff7ff,
    handling: {
      steeringMultiplier: 1.08,
      yawResponseMultiplier: 1.12,
      gripMultiplier: 1.14,
      maxSpeedMultiplier: 0.985,
    },
  },
  {
    id: 'drift',
    name: 'Smoke Line',
    unlockRank: 5,
    tagline: 'Style-chain build',
    perk: 'Longer slides with more drift drive.',
    paintColor: 0x7a1cff,
    accentColor: 0xff6ad5,
    handling: {
      steeringMultiplier: 1.03,
      gripMultiplier: 0.78,
      driftBoostMultiplier: 1.28,
      nitroAccelMultiplier: 0.96,
    },
  },
  {
    id: 'velocity',
    name: 'Black Route V12',
    unlockRank: 8,
    tagline: 'High-speed delivery',
    perk: 'Higher top speed and harder nitro hit.',
    paintColor: 0x080b10,
    accentColor: 0xffb23e,
    handling: {
      accelMultiplier: 0.96,
      maxSpeedMultiplier: 1.08,
      nitroSpeedMultiplier: 1.08,
      nitroAccelMultiplier: 1.16,
      steeringMultiplier: 0.96,
    },
  },
];

export function allDriverLoadouts(): readonly DriverLoadout[] {
  return LOADOUTS;
}

export function driverLoadoutById(id: string | null | undefined): DriverLoadout {
  return LOADOUTS.find((loadout) => loadout.id === id) ?? LOADOUTS[0]!;
}

export function currentDriverRank(): number {
  return rankForXp(getDriverProgress().totalXp);
}

export function isDriverLoadoutUnlocked(loadout: DriverLoadout, rank = currentDriverRank()): boolean {
  return rank >= loadout.unlockRank;
}

export function selectedDriverLoadoutId(): DriverLoadoutId {
  if (typeof sessionStorage === 'undefined') return 'balanced';
  try {
    const id = sessionStorage.getItem(DRIVER_LOADOUT_STORAGE_KEY);
    const selected = driverLoadoutById(id);
    return isDriverLoadoutUnlocked(selected) ? selected.id : 'balanced';
  } catch {
    return 'balanced';
  }
}
